// src/components/PricingFrequency.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Alert
} from "@mui/material";
import {
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  ConfirmationDialog,
  HistoryModal,
  isPermissionDenied,
  getPermissionDeniedText,
  getPermissionDeniedMessage,
  RequestAccessButton,
  getWriteErrorMessage,
  isVersionConflict,
  loadedModifiedDate,
  withLoadedModifiedDate,
} from "@1823-partners/core";

import api, { getPricingFrequency, savePricingFrequency, deleteFrequencyEntry } from "../api";
import { formatDateTimeForDisplay } from "../utils/dateUtils";
import FrequencyHistory from "./FrequencyHistory";
import { adoptModifiedDate, keyEditModifiedDate, mergeUnsavedEdits, trackCellEdit } from "../utils/modifiedDates";
import { assignKeyEditId, duplicateKeysMessage, findDuplicateKeys, makeTrackingKeyOf } from "../utils/rowIdentity";

// Unsaved edits and AG Grid row ids: TEMP_ROW_ID, else the KEY_EDIT_ID a row
// whose ID2 was edited carries, else ID2 (see utils/rowIdentity). A blanked ID2
// therefore never leaves a loaded row without a tracking key.
const frequencyTrackingKey = makeTrackingKeyOf(row => row.ID2);
// The key Save checks for duplicates; null for a blank ID2, which Save reports
// as required instead.
const frequencySaveKey = row => (String(row.ID2 ?? '').trim() === '' ? null : row.ID2);

const PricingFrequency = ({ userId }) => {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  // The denial *object* alongside the message: RequestAccessButton reads
  // suggested_roles off it, which the message string cannot carry. Cleared
  // wherever `error` is cleared or replaced, or a later unrelated failure would
  // still show a button naming a role from an earlier denial.
  const [deniedError, setDeniedError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [updatedRows, setUpdatedRows] = useState({});
  const [newRowCount, setNewRowCount] = useState(0);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedRowForHistory, setSelectedRowForHistory] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [quickFilterText, setQuickFilterText] = useState('');
  const gridRef = useRef(null);
  // Every replacement of the action banner advances this token. A delayed clear may
  // only remove the exact generation that scheduled it, so an older
  // no-change/save/delete timer cannot erase a later permission denial. Same
  // approach as PricingGrid and BrokersGrid — this file schedules three temporary
  // messages, so comparing message text would not distinguish two identical ones.
  const actionErrorGenerationRef = useRef(0);
  // Rows under an edited ID2: tracking key -> the ID2 and MODIFIED_DATE they
  // had before, so editing ID2 back sends that MODIFIED_DATE again
  // (keyEditModifiedDate). Cleared with updatedRows.
  const loadedKeysRef = useRef({});

  const clearActionError = useCallback(() => {
    actionErrorGenerationRef.current += 1;
    setError(null);
    setDeniedError(null);
  }, []);

  const showActionError = useCallback((message, denial = null) => {
    actionErrorGenerationRef.current += 1;
    setError(message);
    setDeniedError(denial);
  }, []);

  const showTemporaryActionError = useCallback((message, timeoutMs) => {
    const generation = actionErrorGenerationRef.current + 1;
    actionErrorGenerationRef.current = generation;
    setError(message);
    setDeniedError(null);
    setTimeout(() => {
      if (actionErrorGenerationRef.current !== generation) return;
      actionErrorGenerationRef.current += 1;
      setError(null);
      setDeniedError(null);
    }, timeoutMs);
  }, []);


  // Per-user grid layout persistence. Own the hook here (rather than letting
  // GridConfigMenu instantiate it) so we can bridge gridConfigs.onGridReady to
  // PamGrid — without that bridge a slow grid mount can miss the default-layout
  // auto-apply. See useGridConfigs / ReconList for the race rationale.
  const gridConfigs = useGridConfigs(api, {
    app: 'pricing',
    gridId: 'frequency',
    gridApiRef: gridRef,
  });

  const gridConfigComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  // Load pricing frequency data. Resolves { ok: true } once the rows are set,
  // or { ok: false, message } after showing why they could not be loaded (the
  // rows on screen are then left as they were).
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      clearActionError();
      const response = await getPricingFrequency();
      const data = response.data || [];
      setRowData(data);
      return { ok: true };
    } catch (err) {
      console.error('Error loading pricing frequency data:', err);
      const message = isPermissionDenied(err)
        ? getPermissionDeniedMessage(err)
        : 'Failed to load pricing frequency data. Please try again.';
      showActionError(message);
      return { ok: false, message };
    } finally {
      setIsLoading(false);
    }
  }, [clearActionError, showActionError]);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // After a 409: reload, then put the unsaved edits (on the reloaded
  // MODIFIED_DATEs) and the unsaved new rows back, so saving again is the user's
  // deliberate re-apply. A row whose ID2 was edited is kept whole, since only its
  // edited fields are tracked. Never retry with a refreshed MODIFIED_DATE.
  // If the reload fails nothing is merged: the rows on screen are not fresh,
  // and merging them as if they were would put the new and ID2-edited rows on
  // screen twice. Resolves loadData's { ok, message }.
  const reloadKeepingEdits = useCallback(async (changesById, currentRows) => {
    const reload = await loadData();
    if (!reload.ok) return reload;
    setRowData(freshRows => [
      ...mergeUnsavedEdits({
        freshRows,
        currentRows,
        changesByKey: changesById,
        trackingKeyOf: frequencyTrackingKey,
        keyFields: ['ID2'],
        adoptServerModifiedDates: true,
      }),
      ...currentRows.filter(row => row.TEMP_ROW_ID),
    ]);
    return reload;
  }, [loadData]);

  // The banner after a 409: the conflict, and if the reload behind it failed,
  // that too (the grid then still shows the unsaved changes as they were).
  const conflictMessage = useCallback((writeError, reload) => (
    reload.ok
      ? writeError
      : `${writeError} ${reload.message} Your unsaved changes are still on screen.`
  ), []);

  // Handle cell value changes
  const handleCellValueChanged = useCallback((params) => {
    const { data, colDef, newValue, oldValue } = params;

    if (newValue === oldValue) {
      return;
    }

    const field = colDef.field;

    // AG Grid has already written newValue into data, so the ID2 the row had
    // before this edit comes from oldValue. A loaded row's first ID2 edit gives
    // it a KEY_EDIT_ID to be tracked by from then on, so a blanked ID2 is still
    // tracked (and retyping it restores the row's loaded MODIFIED_DATE), and an
    // ID2 another row already has keeps the two rows' edits apart.
    const rowBefore = field === 'ID2' ? { ...data, ID2: oldValue } : data;
    const previousKey = frequencyTrackingKey(rowBefore);
    if (field === 'ID2') assignKeyEditId(data);
    const trackingKey = frequencyTrackingKey(data);

    // An edited ID2 names a different row, so it no longer has a loaded
    // MODIFIED_DATE: the save sends null for it, unless the edit takes the row
    // back to its own ID2.
    if (field === 'ID2') {
      data.MODIFIED_DATE = data.TEMP_ROW_ID ? null : keyEditModifiedDate(loadedKeysRef.current, {
        rowBefore,
        rowAfter: data,
        keyFields: ['ID2'],
        previousKey,
        trackingKey,
      });
    }

    // Only track changes for existing rows (not new rows with TEMP_ROW_ID)
    if (!data.TEMP_ROW_ID) {
      setUpdatedRows(prev => trackCellEdit(prev, { previousKey, trackingKey, field, oldValue, newValue }));
      // MODIFIED_BY / MODIFIED_DATE are never restamped here: MODIFIED_DATE is
      // the concurrency token and the writer stamps both.
    }
  }, []);

  // Handle adding new row
  const handleAddRow = useCallback(() => {
    const newRow = {
      TEMP_ROW_ID: `temp_${Date.now()}`,
      ID2: '',
      FREQUENCY: 'Monthly',
      NOTES: '',
      STATUS: 'Active',
      // A new key: no loaded MODIFIED_DATE. The writer stamps both on save.
      MODIFIED_BY: null,
      MODIFIED_DATE: null
    };

    setRowData(prev => {
      const newData = [...prev, newRow];

      // Scroll to the new row
      setTimeout(() => {
        if (gridRef.current?.api) {
          gridRef.current.api.ensureIndexVisible(newData.length - 1);
        }
      }, 100);

      return newData;
    });
    setNewRowCount(prev => prev + 1);
  }, []);

  // Handle delete button click - show confirmation
  const handleDeleteClick = useCallback((rowData) => {
    setRowToDelete(rowData);
    setDeleteConfirmOpen(true);
  }, []);

  // Handle confirmed deletion
  const handleConfirmDelete = useCallback(async () => {
    // The whole banner, not just the denial object: clearing only the latter left
    // the previous permission text on screen, which a successful retry would then
    // show beside its success message.
    clearActionError();

    if (!rowToDelete) return;

    try {
      if (rowToDelete.TEMP_ROW_ID) {
        // Remove new row that hasn't been saved yet
        setRowData(prev => prev.filter(row => row.TEMP_ROW_ID !== rowToDelete.TEMP_ROW_ID));
        setNewRowCount(prev => Math.max(0, prev - 1));
      } else {
        // The tombstone carries the row's loaded MODIFIED_DATE unchanged; the
        // writer stamps MODIFIED_BY and the new MODIFIED_DATE.
        const deleteBody = withLoadedModifiedDate({
          ID2: rowToDelete.ID2,
          FREQUENCY: rowToDelete.FREQUENCY,
          NOTES: rowToDelete.NOTES,
          STATUS: 'Deleted',
        }, rowToDelete);
        const response = await deleteFrequencyEntry(deleteBody);

        // Written: show the tombstone on the writer's stamp and stop tracking the
        // row. A later save must not resend it (/save-frequency writes STATUS
        // Active, which would undo the delete). This row only: another row with
        // the same ID2 keeps its own state.
        const trackingKey = frequencyTrackingKey(rowToDelete);
        setRowData(prev => prev.map(row =>
          !row.TEMP_ROW_ID && frequencyTrackingKey(row) === trackingKey
            ? adoptModifiedDate({ ...row, STATUS: 'Deleted' }, response?.data?.modified_date)
            : row
        ));
        setUpdatedRows(prev => {
          const next = { ...prev };
          delete next[trackingKey];
          return next;
        });
        delete loadedKeysRef.current[trackingKey];

        setSuccessMessage(`Successfully marked ${rowToDelete.ID2} as deleted.`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('Error deleting frequency entry:', err);
      const writeError = getWriteErrorMessage(err, null);
      if (isPermissionDenied(err)) {
        showActionError(getPermissionDeniedText(err), err);
      } else if (isVersionConflict(err)) {
        // Reload so the user sees the other change before deciding again.
        const reload = await reloadKeepingEdits(updatedRows, rowData);
        showActionError(conflictMessage(writeError, reload));
      } else if (writeError) {
        showActionError(writeError);
      } else {
        showTemporaryActionError('Failed to delete entry. Please try again.', 5000);
      }
    } finally {
      setDeleteConfirmOpen(false);
      setRowToDelete(null);
    }
  }, [rowToDelete, rowData, updatedRows, reloadKeepingEdits, conflictMessage, clearActionError, showActionError, showTemporaryActionError]);

  // Handle viewing row history
  const handleViewHistory = useCallback((rowData) => {
    setSelectedRowForHistory(rowData);
    setHistoryModalOpen(true);
  }, []);

  // Handle saving changes
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      clearActionError();

      // Prepare data for saving - only send changed/added rows
      const dataToSave = [];
      const pendingRows = [];

      // Add updated rows (existing rows that were modified)
      Object.keys(updatedRows).forEach(rowId => {
        const row = rowData.find(r => !r.TEMP_ROW_ID && frequencyTrackingKey(r) === rowId);
        if (row) {
          pendingRows.push(row);
          dataToSave.push({
            ID2: row.ID2,
            FREQUENCY: row.FREQUENCY || 'Monthly',
            NOTES: row.NOTES,
            STATUS: row.STATUS || 'Active',
            // The loaded MODIFIED_DATE, unchanged (null after an ID2 edit).
            MODIFIED_DATE: loadedModifiedDate(row)
          });
        }
      });

      // Add new rows (rows with TEMP_ROW_ID)
      const newRows = rowData.filter(row => row.TEMP_ROW_ID);
      newRows.forEach(row => {
        pendingRows.push(row);
        dataToSave.push({
          ID2: row.ID2,
          FREQUENCY: row.FREQUENCY || 'Monthly',
          NOTES: row.NOTES,
          STATUS: row.STATUS || 'Active',
          MODIFIED_DATE: null
        });
      });

      // Validate that we have data to save
      if (dataToSave.length === 0) {
        showTemporaryActionError('No changes to save.', 3000);
        return;
      }

      // ID2 is the writer key: a blank one cannot be saved.
      const blankCount = pendingRows.filter(row => frequencySaveKey(row) === null).length;
      if (blankCount > 0) {
        showTemporaryActionError(
          `Validation errors:\n${blankCount === 1 ? '1 row has' : `${blankCount} rows have`} no ID2. ID2 is required.`,
          10000
        );
        return;
      }

      // Two rows under one ID2 would be two writes to one writer row, the later
      // silently replacing the earlier. Check every row on screen, unchanged
      // and new ones included, and save nothing until the user resolves it.
      const pendingSet = new Set(pendingRows);
      const duplicates = findDuplicateKeys({
        rows: rowData,
        keyOf: frequencySaveKey,
        isPending: row => pendingSet.has(row),
      });
      if (duplicates.length > 0) {
        showActionError(duplicateKeysMessage(duplicates, { keyLabel: 'ID2', describe: row => row.ID2 }));
        return;
      }

      await savePricingFrequency(dataToSave);

      // Store counts before clearing state
      const updatedCount = Object.keys(updatedRows).length;
      const newCount = newRows.length;

      // Clear state and reload data
      setUpdatedRows({});
      loadedKeysRef.current = {};
      setNewRowCount(0);

      // Reload data to get updated information from server
      await loadData();

      setSuccessMessage(`Successfully saved ${dataToSave.length} rows (${updatedCount} updated, ${newCount} new).`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error saving pricing frequency data:', err);
      const writeError = getWriteErrorMessage(err, null);
      if (isPermissionDenied(err)) {
        showActionError(getPermissionDeniedText(err), err);
      } else if (isVersionConflict(err)) {
        const reload = await reloadKeepingEdits(updatedRows, rowData);
        showActionError(conflictMessage(writeError, reload));
      } else if (writeError) {
        showActionError(writeError);
      } else {
        showTemporaryActionError('Failed to save changes. Please try again.', 5000);
      }
    } finally {
      setIsSaving(false);
    }
  }, [rowData, loadData, updatedRows, reloadKeepingEdits, conflictMessage, clearActionError, showActionError, showTemporaryActionError]);

  // Check if a row is new
  const isNewRow = useCallback((data) => {
    return !!data.TEMP_ROW_ID;
  }, []);

  // Row id: the tracking key, unique even while two rows share an ID2 (AG Grid
  // would otherwise show only one), and the key updatedRows highlights by.
  const getRowId = useCallback((data) => frequencyTrackingKey(data), []);

  // Column definitions
  const columnDefs = useMemo(() => [
    {
      headerName: "ID2",
      headerTooltip: "Secondary identifier",
      field: "ID2",
      pinned: 'left',
      minWidth: 150,
      editable: true
    },
    {
      headerName: "Frequency",
      headerTooltip: "Pricing frequency",
      field: "FREQUENCY",
      minWidth: 150,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['Monthly', 'Quarterly']
      }
    },
    {
      headerName: "Notes",
      headerTooltip: "Notes about pricing frequency",
      field: "NOTES",
      minWidth: 300,
      editable: true
    },
    {
      headerName: "Modified By",
      headerTooltip: "User who last modified this entry",
      field: "MODIFIED_BY",
      editable: false,
      minWidth: 150
    },
    {
      headerName: "Modified Date",
      headerTooltip: "Date when this entry was last modified",
      field: "MODIFIED_DATE",
      editable: false,
      minWidth: 180,
      valueFormatter: params => formatDateTimeForDisplay(params.value)
    }
  ], []);

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Pricing Frequency
      </Typography>

      {/* Error Message */}
      {error && (
        <Alert
          severity={deniedError ? 'warning' : 'error'}
          action={deniedError ? <RequestAccessButton error={deniedError} /> : undefined}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Grid with PamGrid */}
      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        height="600px"
        isLoading={isLoading}
        loadingMessage="Loading pricing frequency data..."

        // Toolbar
        showToolbar={true}
        showAddRow={true}
        onAddRow={handleAddRow}
        addRowLabel="Add Row"
        showSave={true}
        onSave={handleSave}
        changedRowCount={Object.keys(updatedRows).length + newRowCount}
        isSaving={isSaving}
        showExport={true}
        exportConfig={{
          fileName: `pricing-frequency-${new Date().toISOString().split('T')[0]}.xlsx`,
          sheetName: 'Pricing Frequency',
          processCellCallback: (params) => {
            if (params.column.getColId() === 'ID2') {
              return params.value ? `'${params.value.toString()}` : '';
            }
            return params.value;
          },
        }}

        // Grid layout persistence (view-config)
        customToolbarComponents={gridConfigComponents}
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}

        // Quick filter
        showQuickFilter={true}
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}

        // Actions column
        showActions={true}
        actions={['history', 'delete']}
        onHistory={handleViewHistory}
        onDelete={handleDeleteClick}

        // Change tracking
        updatedRows={updatedRows}
        getRowId={getRowId}
        isNewRow={isNewRow}

        // Grid options
        rowHeight={32}
        headerHeight={40}
        onCellValueChanged={handleCellValueChanged}
        suppressMovableColumns={false}
        suppressColumnVirtualisation={false}
        enableCellTextSelection={true}
        ensureDomOrder={true}
        enableRangeSelection={true}
      />

      <HistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`Frequency History - ${selectedRowForHistory?.ID2 || 'Entry'}`}
        height="60vh"
      >
        <FrequencyHistory
          id2={selectedRowForHistory?.ID2}
          showTitle={false}
        />
      </HistoryModal>

      {/* Delete Confirmation Dialog using 1823-core */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message={
          <>
            Are you sure you want to delete the entry for ID2: {rowToDelete?.ID2}?
            {rowToDelete?.TEMP_ROW_ID
              ? " (This is a new row that hasn't been saved yet)"
              : " (This will mark the entry as deleted)"}
          </>
        }
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default PricingFrequency;
